/**
 * AFR 申报凭证
 *
 * 设计来源：设计文档 §7.2.1（AFR）/ §7.3（区块：申报信息 / 参与方 / 集装箱 / 货物）
 * - Filing Status / Status Date 取最新回执
 * - Containers 仅 Container No. / Type / SOC（eqOwner）
 */
import type { CertificateBuilder, CertificateContext, CertificateData, CertificateSection } from '../types'
import { registerCertificateBuilder, CERTIFICATE_TITLES } from './registry'
import {
  DASH,
  FieldCollector,
  billWithScac,
  dedupeMissing,
  dictContainerType,
  dictPackingUnit,
  dictPort,
  filingStatus,
  findParty,
  formatDateTime,
  gridSection,
  latestReceipt,
  logsSection,
  partyAddress,
  pick,
  receiptValue,
  rowsOf,
  socLabel,
  tableSection,
  text,
} from './shared'

export const buildAfrCertificate: CertificateBuilder = (
  context: CertificateContext
): CertificateData => {
  const detail = context.detail ?? {}
  const dict = context.dict
  const receipt = latestReceipt(context.receipts)
  const missing: string[] = []

  const info = new FieldCollector(missing)
  info
    .add(
      'Filing Status',
      filingStatus(
        pick(receipt, 'receiptStatus') || pick(detail, 'customsStatus'),
        context.statusLabel,
        pick(receipt, 'sendType')
      )
    )
    .add('Status Date', formatDateTime(receipt?.receiptTime))
    .add('MBL', pick(detail, 'mblNo'))
    .add('HBL', billWithScac(pick(detail, 'hblScacCode'), pick(detail, 'hblNo')))
    .add('Vessel', pick(detail, 'vesselName'))
    .add('Voyage', pick(detail, 'voyage'))
    .add('Port Of Loading', dictPort(dict, detail.loadingPortId))
    .add('Port Of Discharge', dictPort(dict, detail.dischargePortId))
    .add('ETD', formatDateTime(pick(detail, 'etd')))
    .add('ETA', formatDateTime(pick(detail, 'eta')))

  const parties = new FieldCollector(missing)
  parties
    .add('Shipper', partyAddress(findParty(detail, 'SHIPPER'), { dict }))
    .add('Consignee', partyAddress(findParty(detail, 'CONSIGNEE'), { dict }))

  const containerRows = rowsOf(detail, 'containerList').map(row => [
    text(row.containerNo) || DASH,
    dictContainerType(dict, row.containerType) || DASH,
    socLabel(row.eqOwner) || DASH,
  ])

  const cargoRows = rowsOf(detail, 'cargoList').map(row => [
    text(row.cargoName) || DASH,
    text(row.hsCode) || DASH,
    text(row.quantity) || DASH,
    dictPackingUnit(dict, row.unitTypeId) || DASH,
    text(row.grossWeight) || DASH,
  ])

  const sections: CertificateSection[] = [
    gridSection(2, info.fields, 'DECLARATION'),
    gridSection(2, parties.fields, 'PARTY INFORMATION'),
    tableSection('CONTAINERS', ['Container No.', 'Type', 'SOC'], containerRows, missing),
    tableSection(
      'CARGOS',
      ['Goods Description', 'HS Code', 'Qty', 'Unit', 'Weight'],
      cargoRows,
      missing
    ),
    logsSection(context.receipts, missing),
  ]

  return {
    product: 'AFR',
    title: CERTIFICATE_TITLES.AFR,
    issuedAt: formatDateTime(
      receiptValue(context.receipts, 'receiptTime') ||
        pick(detail, 'receiptTime') ||
        pick(detail, 'dateModified')
    ),
    sections,
    missing: dedupeMissing(missing),
  }
}

registerCertificateBuilder('AFR', buildAfrCertificate)
